/**
 * SearchScreen.web.tsx - Luxe web zoekscherm
 */

import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, TextInput, ActivityIndicator, Animated } from 'react-native';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import { productService } from '../services/ProductService';
import type { ProductDisplay } from '../models';

const bepaalCijferKleur = (grade: string) => {
  if (!grade) return '#999';
  if (grade.startsWith('A')) return '#10B981';
  if (grade.startsWith('B')) return '#34D399';
  if (grade.startsWith('C')) return '#FBBF24';
  if (grade.startsWith('D')) return '#F59E0B';
  return '#EF4444';
};

const ProductItemWeb: React.FC<{ item: ProductDisplay, onPress: () => void }> = ({ item, onPress }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <TouchableOpacity
      style={stijlen.productKaart}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={stijlen.productIcoon}>
        {item.imageUrl && !imageError ? (
          // @ts-ignore - Dit is web, dus img tag mag gewoon.
          // Is sneller en makkelijker dan Image component.
          <img 
            src={item.imageUrl} 
            alt={item.name}
            onError={() => setImageError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          />
        ) : (
          <Text style={{ fontSize: 28 }}>🧴</Text>
        )}
      </View>
      <View style={stijlen.productInfo}>
        <Text style={stijlen.productNaam} numberOfLines={1}>{item.name}</Text>
        <Text style={stijlen.productMerk}>{item.brand}</Text>
        <Text style={stijlen.productCategorie}>{item.category}</Text>
      </View>
      <View style={[stijlen.cijferBadge, { backgroundColor: bepaalCijferKleur(item.grade) }]}>
        <Text style={stijlen.cijferTekst}>{item.grade}</Text>
      </View>
    </TouchableOpacity>
  );
};

export const SearchScreen: React.FC<any> = ({ navigation, route }) => {
  const { t } = useTranslation();
  // @ts-ignore
  const params = route?.params; // Pak params uit de route
  const [zoekOpdracht, setZoekOpdracht] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [vertraagdeOpdracht, setVertraagdeOpdracht] = useState('');
  const [zoekResultaten, setZoekResultaten] = useState<ProductDisplay[]>([]);
  const [isAanHetLaden, setIsAanHetLaden] = useState(false);
  const [isGefocust, setIsGefocust] = useState(false);

  // Initialisatie van params (bijv. vanuit Home 'Alles Checken')
  useEffect(() => {
    if (params?.autoSearch) {
        // Alles checken modus
        voerZoekUit(true);
    } else if (params?.query) {
        setZoekOpdracht(params.query);
    }
  }, [params]);

  // Zoekopdracht bufferen zodat we de browser niet slopen (Debounce)
  // We wachten 300ms nadat je stopt met typen voordat we echt gaan zoeken
  useEffect(() => {
    const timer = setTimeout(() => {
      setVertraagdeOpdracht(zoekOpdracht);
    }, 300);
    return () => clearTimeout(timer);
  }, [zoekOpdracht]);

  // Dit effect runt pas als 'vertraagdeOpdracht' verandert OF category
  useEffect(() => {
    if (!activeCategory) {
        voerZoekUit();
    }
  }, [vertraagdeOpdracht]);

  // We maken de zoekfunctie, nu ook geschikt voor alles checken en categorieën
  const voerZoekUit = async (forceerAlles: boolean = false) => {
      // Als we een categorie hebben, wint die
      if (activeCategory) {
          setIsAanHetLaden(true);
          try {
              // @ts-ignore - Functie moet bestaan in service
              const resultaten = await productService.getProductsByCategory(activeCategory);
              setZoekResultaten(resultaten);
          } catch (fout) {
              console.error('Categorie fout:', fout);
          } finally {
              setIsAanHetLaden(false);
          }
          return;
      }

      // Niks ingevuld en niet forceren? Dan maken we de lijst leeg
      if (!vertraagdeOpdracht.trim() && !forceerAlles) {
        setZoekResultaten([]);
        return;
      }

      setIsAanHetLaden(true);
      try {
        let resultaten;
        if (forceerAlles || (vertraagdeOpdracht.trim() === '*' )) {
             // Hack: zoek op lege string of speciaal teken voor "alles" indien supported
             // Of gebruik getDiscoveryProducts met hoog limiet
             resultaten = await productService.getDiscoveryProducts(100); 
        } else {
             // Hier bellen we de database service
            resultaten = await productService.searchProducts(vertraagdeOpdracht);
        }
        setZoekResultaten(resultaten);
      } catch (fout) {
        console.error('Zoek fout:', fout);
      } finally {
        // Klaar met laden, spinner mag weg
        setIsAanHetLaden(false);
      }
    };

    const kiesCategorie = (cat: string) => {
        if (activeCategory === cat) {
            setActiveCategory(null); // Deselecteren
        } else {
            setActiveCategory(cat);
            setZoekOpdracht(''); // Tekst weg
        }
        // Direct triggeren
        setTimeout(() => voerZoekUit(), 50);
    };

  const behandelWissen = () => {
    setZoekOpdracht('');
    setZoekResultaten([]);
  };

  const behandelProductKlik = (productId: number) => {
    navigation.navigate('ProductDetail', { productId });
  };

// function moved up

  const toonProductItem = ({ item }: { item: ProductDisplay }) => (
    <ProductItemWeb item={item} onPress={() => behandelProductKlik(item.id)} />
  );

  const toonLegeStatus = () => {
    if (isAanHetLaden) return null;

    if (!zoekOpdracht.trim()) {
      return (
        <View style={stijlen.legeStatus}>
          <View style={stijlen.leegIcoonContainer}>
            <Text style={stijlen.leegIcoon}>🔍</Text>
          </View>
          <Text style={stijlen.legeTitel}>{t('search.title') || 'Zoek Producten'}</Text>
          <Text style={stijlen.legeSubtitel}>
            {t('search.placeholder') || 'Zoek op naam, merk of barcode'}
          </Text>
          
          <View style={stijlen.suggestieSectie}>
            <Text style={stijlen.suggestieTitel}>{t('search.categories', 'Categorieën')}</Text>
            <View style={stijlen.suggestieTags}>
              {/* Categorie knoppen */}
              {['Hair Care', 'Sun Care', 'Body Care', 'Dental', 'Face Care'].map((cat) => (
                <TouchableOpacity 
                  key={cat} 
                  style={[
                      stijlen.suggestieTag,
                      activeCategory === cat && stijlen.suggestieTagActief
                  ]}
                  onPress={() => kiesCategorie(cat)}
                >
                  <Text style={[
                      stijlen.suggestieTekst,
                      activeCategory === cat && stijlen.suggestieTekstActief
                  ]}>{cat}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={stijlen.tipsSectie}>
            <Text style={stijlen.tipsTitel}>💡 Zoek Tips</Text>
            <View style={stijlen.tipItem}>
              <Text style={stijlen.tipPunt}>•</Text>
              <Text style={stijlen.tipTekst}>Zoek op naam: "Ocean Breeze Shampoo"</Text>
            </View>
            <View style={stijlen.tipItem}>
              <Text style={stijlen.tipPunt}>•</Text>
              <Text style={stijlen.tipTekst}>Zoek op merk: "EcoLife"</Text>
            </View>
            <View style={stijlen.tipItem}>
              <Text style={stijlen.tipPunt}>•</Text>
              <Text style={stijlen.tipTekst}>Zoek op barcode: "8710447426487"</Text>
            </View>
          </View>
        </View>
      );
    }

    if (zoekResultaten.length === 0) {
      return (
        <View style={stijlen.legeStatus}>
          <Text style={stijlen.leegIcoon}>🌊</Text>
          <Text style={stijlen.legeTitel}>{t('search.noResults') || 'Geen Resultaten'}</Text>
          <Text style={stijlen.legeSubtitel}>
            Probeer iets anders of scan een barcode
          </Text>
          <TouchableOpacity 
            style={stijlen.scanKnop}
            onPress={() => navigation.navigate('HomeTab', { screen: 'Scanner' })}
          >
            <Text style={stijlen.scanKnopIcoon}>📷</Text>
            <Text style={stijlen.scanKnopTekst}>Scan Liever</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return null;
  };

  return (
    <View style={stijlen.container}>
      {/* De Header */}
      <View style={stijlen.kop}>
        <View style={stijlen.kopGradient}>
          <Text style={stijlen.kopTitel}>🔍 Zoeken</Text>

        </View>
        <View style={stijlen.kopGolf} />
      </View>

      {/* Search Bar */}
      <View style={stijlen.zoekContainer}>
        <View style={[stijlen.zoekBalk, isGefocust && stijlen.zoekBalkGefocust]}>
          <Text style={stijlen.zoekIcoon}>🔍</Text>
          <TextInput
            style={stijlen.zoekInvoer}
            value={zoekOpdracht}
            onChangeText={setZoekOpdracht}
            placeholder={t('search.placeholder') || 'Zoek producten, merken...'}
            placeholderTextColor="#94A3B8"
            onFocus={() => setIsGefocust(true)}
            onBlur={() => setIsGefocust(false)}
            autoFocus={true}
          />
          {zoekOpdracht.length > 0 && (
            <TouchableOpacity onPress={behandelWissen} style={stijlen.wisKnop}>
              <Text style={stijlen.wisKnopTekst}>✕</Text>
            </TouchableOpacity>
          )}
        </View>
        
        {zoekOpdracht.length > 0 && (
          <Text style={stijlen.resultaatAantal}>
            {isAanHetLaden ? 'Zoeken...' : `${zoekResultaten.length} resultaten`}
          </Text>
        )}
      </View>

      {/* Content */}
      <View style={stijlen.inhoud}>
        {isAanHetLaden ? (
          <View style={stijlen.laadContainer}>
            <ActivityIndicator size="large" color="#6366F1" />
            <Text style={stijlen.laadTekst}>Spullen zoeken...</Text>
          </View>
        ) : (
          <FlatList
            data={zoekResultaten}
            renderItem={toonProductItem}
            keyExtractor={item => item.id.toString()}
            contentContainerStyle={stijlen.lijstInhoud}
            ListEmptyComponent={toonLegeStatus}
            showsVerticalScrollIndicator={false}
          />
        )}
      </View>
    </View>
  );
};

const stijlen = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
    minHeight: '100%' as any,
  },
  kop: {
    position: 'relative',
    height: 130,
    overflow: 'hidden',
  },
  kopGradient: {
    flex: 1,
    // @ts-ignore - Web gradient.
    // CSS magie die ReactNative niet standaad snapt.
    backgroundImage: 'linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 20,
  },
  kopTitel: {
    fontSize: 24,
    fontWeight: '700',
    color: 'white',
  },
  kopSubtitel: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.85)',
    marginTop: 4,
  },
  kopGolf: {
    position: 'absolute',
    bottom: -1,
    left: 0,
    right: 0,
    height: 30,
    backgroundColor: '#F8FAFC',
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
  },
  zoekContainer: {
    paddingHorizontal: 20,
    paddingBottom: 16,
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
  },
  zoekBalk: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 14,
    // @ts-ignore - Web schaduw
    boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  zoekBalkGefocust: {
    borderColor: '#6366F1',
    // @ts-ignore - Web gloed
    boxShadow: '0 4px 20px rgba(99, 102, 241, 0.2)',
  },
  zoekIcoon: {
    fontSize: 18,
    marginRight: 12,
  },
  zoekInvoer: {
    flex: 1,
    fontSize: 16,
    color: '#1E293B',
    // @ts-ignore - Weg met die lelijke blauwe rand
    outlineStyle: 'none',
  },
  wisKnop: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#E2E8F0',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  wisKnopTekst: {
    color: '#64748B',
    fontSize: 14,
    fontWeight: '600',
  },
  resultaatAantal: {
    marginTop: 12,
    fontSize: 13,
    color: '#64748B',
    textAlign: 'center',
  },
  inhoud: {
    flex: 1,
    paddingHorizontal: 20,
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
  },
  lijstInhoud: {
    paddingBottom: 100,
    flexGrow: 1,
  },
  laadContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  laadTekst: {
    marginTop: 16,
    fontSize: 14,
    color: '#64748B',
  },
  productKaart: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    // @ts-ignore - Muis handje en animatie
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    cursor: 'pointer',
    // @ts-ignore
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  productIcoon: {
    width: 56,
    height: 56,
    backgroundColor: '#F1F5F9',
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  productInfo: {
    flex: 1,
  },
  productNaam: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 2,
  },
  productMerk: {
    fontSize: 14,
    color: '#64748B',
  },
  productCategorie: {
    fontSize: 12,
    color: '#94A3B8',
    marginTop: 2,
  },
  cijferBadge: {
    width: 44,
    height: 44,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cijferTekst: {
    color: 'white',
    fontWeight: '700',
    fontSize: 14,
  },
  legeStatus: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingTop: 40,
  },
  leegIcoonContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: '#EEF2FF',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  leegIcoon: {
    fontSize: 48,
  },
  legeTitel: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
    textAlign: 'center',
  },
  legeSubtitel: {
    fontSize: 15,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 32,
  },
  suggestieSectie: {
    width: '100%',
    marginBottom: 32,
  },
  suggestieTitel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
    marginBottom: 12,
  },
  suggestieTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  suggestieTag: {
    backgroundColor: '#EEF2FF',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#C7D2FE',
  },
  suggestieTagActief: {
    backgroundColor: '#6366F1',
    borderColor: '#4F46E5',
  },
  suggestieTekst: {
    color: '#6366F1',
    fontSize: 14,
    fontWeight: '500',
  },
  suggestieTekstActief: {
    color: 'white',
  },
  tipsSectie: {
    width: '100%',
    backgroundColor: '#FFFBEB',
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: '#FEF3C7',
  },
  tipsTitel: {
    fontSize: 15,
    fontWeight: '600',
    color: '#92400E',
    marginBottom: 12,
  },
  tipItem: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  tipPunt: {
    color: '#F59E0B',
    marginRight: 8,
    fontWeight: '700',
  },
  tipTekst: {
    flex: 1,
    fontSize: 13,
    color: '#78716C',
    lineHeight: 18,
  },
  scanKnop: {
    flexDirection: 'row',
    alignItems: 'center',
    // @ts-ignore - Web gradient knop
    backgroundImage: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
    paddingVertical: 14,
    paddingHorizontal: 28,
    borderRadius: 25,
    // @ts-ignore - Web schaduw
    boxShadow: '0 6px 20px rgba(99, 102, 241, 0.35)',
    marginTop: 20,
  },
  scanKnopIcoon: {
    fontSize: 18,
    marginRight: 8,
  },
  scanKnopTekst: {
    color: 'white',
    fontSize: 15,
    fontWeight: '600',
  },
});
